import { SLOTH_STATES, type SlothState } from './sloth-states';

/**
 * 樹懶素材清單（spec 22.2、22.3）。
 *
 * 這個檔案不手動列出任何檔名。素材由 `scripts/build-sloth-assets.mjs` 產生到
 * `src/assets/sloth/<slot>/<nn>.webp`，這裡用 `import.meta.glob` 在建置時撿走，
 * 所以「新增一組動畫」＝「多丟幾張原稿進 images/sloth/<slot>/ 再跑一次腳本」，
 * 不需要改任何程式碼。
 *
 * 三個概念要分清楚：
 *
 * - **狀態（SlothState）**：由 `resolveSlothState` 依資料推導，共十個 key，寫死在 spec。
 * - **插槽（slot）**：素材資料夾名稱。可以是狀態名（`neutral`），
 *   也可以是狀態名加尺寸後綴（`neutral-bust`，給 ≤48px 的收合場合）。
 * - **格（frame）**：同一個插槽裡的多張圖。一張＝靜態，多張＝動畫。
 *
 * 狀態 → 插槽的對應允許缺漏：找不到自己的素材時沿著 `SLOT_FALLBACK` 往下找，
 * 最後一定會落到 `neutral`。所以十個狀態現在就都有真正的角色圖，
 * 之後補上哪一個狀態的原稿，哪一個狀態就自動換掉，不必回頭改這裡。
 */

/** 播放方式。新增模式時，`use-sloth-frame.ts` 要一起補上對應的排程邏輯。 */
export type SlothPlayback =
  /** 只顯示第一格。單張素材、或明確不想動的狀態用這個。 */
  | { readonly mode: 'static' }
  /**
   * 隨機換格：每次停留 `minMs`～`maxMs` 之間的隨機時間，再換到另一格。
   * 適合眨眼、呼吸這種不該有固定節拍的待機動作——固定節拍會像壞掉的跑馬燈。
   */
  | { readonly mode: 'random'; readonly minMs: number; readonly maxMs: number }
  /** 逐格循環：固定 `frameMs` 依序播放。適合拍手、翻頁這種有先後順序的動作。 */
  | { readonly mode: 'loop'; readonly frameMs: number };

export const STATIC_PLAYBACK: SlothPlayback = { mode: 'static' };

/**
 * 沒有特別指定的插槽用這組：1～10 秒之間隨機換格。
 * 上限刻意壓在 10 秒——再久使用者會以為畫面凍住了。
 */
export const DEFAULT_PLAYBACK: SlothPlayback = { mode: 'random', minMs: 1000, maxMs: 10000 };

/** 個別插槽的播放設定；沒列到的用 `DEFAULT_PLAYBACK`。 */
export const SLOT_PLAYBACK: Readonly<Record<string, SlothPlayback>> = {
  neutral: { mode: 'random', minMs: 1000, maxMs: 10000 },
};

/**
 * 狀態缺素材時往哪裡退。沒列到的一律退到 `neutral`。
 * 這裡只寫「退到別的狀態比退到 neutral 更貼切」的例外。
 */
export const SLOT_FALLBACK: Readonly<Partial<Record<SlothState, SlothState>>> = {
  welcome: 'neutral',
  night: 'neutral',
};

/** 素材尺寸。`sm` 給助理區收合後的 32px，`md` 給一般 96–144px 的場合。 */
export type SlothAssetSize = 'sm' | 'md';

export interface SlothFrame {
  /** 格序，就是檔名前面的數字（`01`、`02`…）。 */
  readonly id: string;
  /** 1x 圖。 */
  readonly src: string;
  /** `src` 與 2x 圖組成的 srcset；沒有 2x 時為 undefined。 */
  readonly srcSet: string | undefined;
}

export interface SlothSequence {
  readonly slot: string;
  readonly frames: readonly SlothFrame[];
  readonly playback: SlothPlayback;
}

/** 建置時把 src/assets/sloth 底下所有 webp 撿進來，值是打包後的 URL。 */
const ASSET_URLS = import.meta.glob<string>('../../assets/sloth/*/*.webp', {
  eager: true,
  query: '?url',
  import: 'default',
});

interface FrameSources {
  src?: string;
  src2x?: string;
}

function buildSequences(): ReadonlyMap<string, SlothSequence> {
  const bySlot = new Map<string, Map<string, FrameSources>>();

  for (const [filePath, url] of Object.entries(ASSET_URLS)) {
    const matched = /\/sloth\/([^/]+)\/([^/]+)\.webp$/.exec(filePath.replaceAll('\\', '/'));
    if (!matched) continue;
    const [, slot, base] = matched;
    if (!slot || !base) continue;

    const is2x = base.endsWith('@2x');
    const frameId = is2x ? base.slice(0, -'@2x'.length) : base;

    let frames = bySlot.get(slot);
    if (!frames) {
      frames = new Map();
      bySlot.set(slot, frames);
    }
    const entry = frames.get(frameId) ?? {};
    if (is2x) entry.src2x = url;
    else entry.src = url;
    frames.set(frameId, entry);
  }

  const sequences = new Map<string, SlothSequence>();
  for (const [slot, frames] of bySlot) {
    const ordered = [...frames.entries()]
      .sort(([a], [b]) => a.localeCompare(b, 'en'))
      .flatMap<SlothFrame>(([id, sources]) => {
        // 只有 2x 沒有 1x 的話就拿 2x 當 src，總比整格消失好。
        const src = sources.src ?? sources.src2x;
        if (!src) return [];
        return [
          {
            id,
            src,
            srcSet: sources.src2x ? `${src} 1x, ${sources.src2x} 2x` : undefined,
          },
        ];
      });

    if (ordered.length === 0) continue;
    sequences.set(slot, {
      slot,
      frames: ordered,
      playback: ordered.length > 1 ? (SLOT_PLAYBACK[slot] ?? DEFAULT_PLAYBACK) : STATIC_PLAYBACK,
    });
  }

  return sequences;
}

const SEQUENCES = buildSequences();

/**
 * 依 `SLOT_FALLBACK` 展開狀態的搜尋順序，最後一定以 `neutral` 收尾。
 * 遇到環（a→b→a）就停在已經走過的地方，不會無限迴圈。
 */
function fallbackChain(state: SlothState): SlothState[] {
  const chain: SlothState[] = [];
  const seen = new Set<SlothState>();
  let current: SlothState | undefined = state;

  while (current && !seen.has(current)) {
    seen.add(current);
    chain.push(current);
    current = SLOT_FALLBACK[current];
  }
  if (!seen.has('neutral')) chain.push('neutral');
  return chain;
}

/**
 * 狀態＋尺寸 → 實際要播的素材。
 *
 * `sm` 先把整條鏈的半身圖找過一輪，找不到才退回全身圖：
 * 32px 之下「認得出是樹懶」比「狀態正確」重要得多。
 * 完全沒有素材時回傳 null，由 `SlothAssetSlot` 畫回佔位輪廓。
 */
export function resolveSlothSequence(
  state: SlothState,
  size: SlothAssetSize = 'md',
): SlothSequence | null {
  const chain = fallbackChain(state);
  const candidates =
    size === 'sm' ? [...chain.map((slot) => `${slot}-bust`), ...chain] : [...chain];

  for (const slot of candidates) {
    const sequence = SEQUENCES.get(slot);
    if (sequence) return sequence;
  }
  return null;
}

/** 目前還在吃別人素材的狀態；文件與開發時盤點用。 */
export function missingSlothArt(): SlothState[] {
  return SLOTH_STATES.filter((state) => !SEQUENCES.has(state));
}
